import React, { useState } from 'react';
import PageShell from './PageShell';
import { channels, titles, users, interactions, castCrew } from '../recs/engine';
import { useAnalytics } from '../recs/analytics';
import './DataPage.css';

interface Props {
  activeUserId: string;
}

type TabKey = 'titles' | 'users' | 'channels' | 'interactions' | 'castCrew' | 'session';

type LiveTabKey = 'impressions' | 'clicks' | 'hovers' | 'playback' | 'searchQueries';

const LIVE_TAB_EMPTY_HINT: Record<LiveTabKey, string> = {
  impressions: 'Scroll a row on the homepage to log one.',
  clicks: 'Click a title card on the homepage to log one.',
  hovers: 'Hover a title card for a moment on the homepage to log one.',
  playback: 'Open a title and press Play in the detail modal to log one.',
  searchQueries: 'Try the search box in the header — e.g. "tamil drama" or a title name — then come back here.',
};

const TABS: { key: TabKey; label: string; source: string; count: number }[] = [
  { key: 'titles', label: 'Titles', source: 'mw_titles.csv', count: titles.length },
  { key: 'users', label: 'Users', source: 'mw_users.csv', count: users.length },
  { key: 'channels', label: 'Channels', source: 'channels.csv', count: channels.length },
  { key: 'interactions', label: 'Interactions', source: 'mw_interactions.csv', count: interactions.length },
  { key: 'castCrew', label: 'Cast & Crew', source: 'mw_cast_crew.csv', count: castCrew.length },
];

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function Table({
  rows,
  highlightKey,
  highlightValue,
}: {
  rows: Record<string, unknown>[];
  highlightKey?: string;
  highlightValue?: string;
}) {
  if (rows.length === 0) return null;
  const columns = Object.keys(rows[0]);
  return (
    <div className="data-page__table-wrap">
      <table className="data-page__table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const isHighlighted = highlightKey && String(row[highlightKey]) === highlightValue;
            return (
              <tr key={i} className={isHighlighted ? 'data-page__row--highlight' : undefined}>
                {columns.map((c) => (
                  <td key={c}>{String(row[c] ?? '')}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function DataPage({ activeUserId }: Props) {
  const [tab, setTab] = useState<TabKey>('users');
  const [liveTab, setLiveTab] = useState<LiveTabKey>('impressions');
  const { session, elapsedSeconds, impressions, clicks, dwells, playbackEvents, searches, titlesSeenCount } =
    useAnalytics();

  const activeTab = TABS.find((t) => t.key === tab);

  const liveTabs: { key: LiveTabKey; label: string; count: number }[] = [
    { key: 'impressions', label: 'Impressions', count: impressions.length },
    { key: 'clicks', label: 'Clicks', count: clicks.length },
    { key: 'hovers', label: 'Hovers', count: dwells.length },
    { key: 'playback', label: 'Playback', count: playbackEvents.length },
    { key: 'searchQueries', label: 'Search Queries', count: searches.length },
  ];

  const impressionRows = impressions.map((e, i) => ({
    impression_id: `LIVE${impressions.length - i}`,
    user_id: e.userId,
    title_id: e.titleId,
    title_name: e.titleName,
    surface: e.surface,
    row_position: e.rowPosition,
    timestamp: new Date(e.timestamp).toLocaleTimeString(),
  }));

  const clickRows = clicks.map((e, i) => ({
    click_id: `LIVE${clicks.length - i}`,
    user_id: e.userId,
    title_id: e.titleId,
    title_name: e.titleName,
    surface: e.surface,
    row_position: e.rowPosition,
    timestamp: new Date(e.timestamp).toLocaleTimeString(),
  }));

  const hoverRows = dwells.map((e, i) => ({
    hover_id: `LIVE${dwells.length - i}`,
    user_id: e.userId,
    title_id: e.titleId,
    title_name: e.titleName,
    dwell_seconds: (e.durationMs / 1000).toFixed(1),
    timestamp: new Date(e.timestamp).toLocaleTimeString(),
  }));

  const playbackRows = playbackEvents.map((e, i) => ({
    playback_id: `LIVE${playbackEvents.length - i}`,
    user_id: e.userId,
    title_id: e.titleId,
    title_name: e.titleName,
    action: e.action,
    elapsed_seconds: e.elapsedSeconds,
    timestamp: new Date(e.timestamp).toLocaleTimeString(),
  }));

  const searchRows = searches.map((s, i) => ({
    search_id: `LIVE${searches.length - i}`,
    user_id: s.userId,
    query_text: s.queryText,
    timestamp: new Date(s.timestamp).toLocaleTimeString(),
    result_title_id: s.resultTitleId ?? '',
    result_title_name: s.resultTitleName ?? '',
    result_count: s.resultCount,
  }));

  const rowsByStaticTab: Record<Exclude<TabKey, 'session'>, Record<string, unknown>[]> = {
    titles: titles as unknown as Record<string, unknown>[],
    users: users as unknown as Record<string, unknown>[],
    channels: channels as unknown as Record<string, unknown>[],
    castCrew: castCrew as unknown as Record<string, unknown>[],
    interactions: (interactions as unknown as Record<string, unknown>[])
      .filter((i) => i.user_id === activeUserId)
      .concat((interactions as unknown as Record<string, unknown>[]).filter((i) => i.user_id !== activeUserId)),
  };

  const rowsByLiveTab: Record<LiveTabKey, Record<string, unknown>[]> = {
    impressions: impressionRows,
    clicks: clickRows,
    hovers: hoverRows,
    playback: playbackRows,
    searchQueries: searchRows,
  };
  const activeLiveRows = rowsByLiveTab[liveTab];
  const sessionTabCount = liveTabs.reduce((sum, t) => sum + t.count, 0);

  return (
    <PageShell
      title="Data"
      subtitle="This is the real data the demo runs on. Nothing here is scripted per profile — every row below is what actually decides what shows up on the home page."
    >
      <div className="data-page__tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={'data-page__tab' + (t.key === tab ? ' data-page__tab--active' : '')}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            <span className="data-page__tab-count">{t.count}</span>
          </button>
        ))}
        <button
          className={'data-page__tab' + (tab === 'session' ? ' data-page__tab--active' : '')}
          onClick={() => setTab('session')}
        >
          Session
          <span className="data-page__tab-count">{sessionTabCount}</span>
        </button>
      </div>

      {tab === 'session' ? (
        <>
          <p className="data-page__source">
            Live signals captured from your current browser session — impressions, clicks, hover-dwell, playback,
            and search queries. Resets on reload; also batched every few seconds to S3 via the just_watch API's{' '}
            <code>/events</code> route (see <code>recs/analytics.tsx</code>).
          </p>

          <dl className="data-page__session-grid">
            <dt>User</dt>
            <dd>{activeUserId}</dd>
            <dt>Device</dt>
            <dd>{session.device}</dd>
            <dt>Network</dt>
            <dd>{session.network}</dd>
            <dt>Duration</dt>
            <dd>{formatElapsed(elapsedSeconds)}</dd>
            <dt>Titles seen</dt>
            <dd>{titlesSeenCount}</dd>
          </dl>

          <div className="data-page__subtabs">
            {liveTabs.map((t) => (
              <button
                key={t.key}
                className={'data-page__subtab' + (t.key === liveTab ? ' data-page__subtab--active' : '')}
                onClick={() => setLiveTab(t.key)}
              >
                {t.label}
                <span className="data-page__tab-count">{t.count}</span>
              </button>
            ))}
          </div>

          {activeLiveRows.length === 0 ? (
            <p className="data-page__empty">Nothing captured yet this session. {LIVE_TAB_EMPTY_HINT[liveTab]}</p>
          ) : (
            <Table rows={activeLiveRows} highlightKey="user_id" highlightValue={activeUserId} />
          )}
        </>
      ) : (
        <>
          <p className="data-page__source">
            source: <code>data/{activeTab!.source}</code>
            {tab === 'interactions' && " · rows for the profile you're currently viewing as are pinned to the top"}
            {tab === 'castCrew' && ' · fabricated for this demo, see data/generate_mw_metadata.py'}
          </p>

          <Table
            rows={rowsByStaticTab[tab]}
            highlightKey={tab === 'users' || tab === 'interactions' ? 'user_id' : undefined}
            highlightValue={activeUserId}
          />
        </>
      )}
    </PageShell>
  );
}
